import React, {useState, useEffect} from "react";
import {connect} from "react-redux";
import {FormattedMessage, useIntl} from "react-intl";
import {Button, TextField} from "@mui/material";

import createReceipt from "api/receipt/createReceipt";
import updateReceiptNumber from "api/order/updateReceiptNumber";
import deleteReceipt from "api/receipt/deleteReceipt";

function ReceiptCard({order, member, retailer, initialReceipt}) {
    const intl = useIntl();

    // retailer prop = { '@id': '/retailers/123', name: 'Nom' } pour admin
    // Sinon on prend le premier retailer du membre connecté
    const retailerIri = retailer?.['@id'] ?? member?.retailers?.[0]?.['@id'] ?? null;

    const [receipt, setReceipt] = useState(initialReceipt);
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(initialReceipt?.value ?? '');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setReceipt(initialReceipt);
        setValue(initialReceipt?.value ?? '');
    }, [initialReceipt]);

    if (!order) return null;

    const handleValidate = () => {
        const originalValue = receipt?.value ?? '';

        if (value === originalValue) {
            setEditing(false);
            return;
        }

        setSaving(true);

        if (value === '' && receipt?.id) {
            deleteReceipt(receipt.id).then(() => {
                setReceipt(null);
                setEditing(false);
            }).catch(() => {
                // keep editing open on failure
            }).finally(() => setSaving(false));
            return;
        }

        const save = receipt?.id
            ? updateReceiptNumber(receipt.id, value)
            : createReceipt(order['@id'], retailerIri, value);
        save.then((saved) => {
            setReceipt(saved);
            setValue(saved.value ?? '');
            setEditing(false);
        }).catch(() => {
            // keep editing open on failure
        }).finally(() => setSaving(false));
    };

    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            backgroundColor: 'white',
            borderRadius: '8px',
            marginBottom: '12px',
            fontSize: '14px',
        }}>
            <span style={{color: '#757575', fontWeight: 600, flexShrink: 0}}>
                {retailer?.name
                    ? <>{retailer.name} — <FormattedMessage id="picking.order.show.receipt.title"/></>
                    : <FormattedMessage id="picking.order.show.receipt.title"/>
                }
            </span>
            {editing ? (
                <>
                    <TextField
                        variant="outlined"
                        size="small"
                        placeholder={intl.formatMessage({id: 'picking.order.show.receipt.placeholder'})}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        autoFocus
                        sx={{flex: 1, maxWidth: '400px'}}
                    />
                    <Button
                        variant="contained"
                        onClick={handleValidate}
                        disabled={saving}
                        sx={{
                            backgroundColor: '#3c81fb',
                            color: '#fff',
                            borderRadius: '32px',
                            height: '36px',
                            padding: '8px 16px',
                            fontSize: '14px',
                            fontWeight: 600,
                            textTransform: 'none',
                            boxShadow: 'none',
                            '&:hover': {backgroundColor: '#2a6de0', boxShadow: 'none'},
                        }}
                    >
                        <FormattedMessage id="picking.order.show.receipt.validate"/>
                    </Button>
                </>
            ) : (
                <>
                    {receipt?.value && (
                        <span style={{color: '#000', fontWeight: 700}}>
                            {receipt.value}
                        </span>
                    )}
                    <Button
                        variant="text"
                        onClick={() => setEditing(true)}
                        disabled={!receipt && !retailerIri}
                        sx={{color: '#3c81fb', textTransform: 'none', fontWeight: 600, fontSize: '14px', padding: '0'}}
                    >
                        <FormattedMessage id={receipt ? 'picking.order.show.receipt.edit' : 'picking.order.show.receipt.add'}/>
                    </Button>
                </>
            )}
        </div>
    );
}

const mapStateToProps = state => ({
    member: state.authentication.member,
});

export default connect(mapStateToProps)(ReceiptCard);
